import React, { useEffect, useMemo, useState } from 'react';
import Axios from 'axios';
import { useParams } from 'react-router-dom';
import * as Routes from '@/modules/routes';
import usePrograms from '@/components/admin/ProgramManagement/usePrograms';
import useProgramContentStore from '@/components/admin/ProgramManagement/AddLessons/useProgramContentStore';
import { IProgram } from '@/components/admin/ProgramManagement/types';
import RoutedSPADialog from '../../shared/RoutedSPADialog';
import SPADialog from '../../shared/SPADialog';
import SPASelect from '../../shared/SPASelect';
import SPAReuseContent from './SPAReuseContent';
import useProgramLessons from './useProgramLessons';

interface ISelectOption {
  label: string;
  value: number;
}

const formatOptions = (programs: IProgram[]) => programs.map(program => ({
  label: `[${program.code}] ${program.full_title_with_edition}`,
  value: program.id,
}));

// AIDEV-NOTE: SPA counterpart of ProgramManagement/AddLessons' AddLessonsModal, mounted as a
// child route (/content/programs/:programId/reuse-lessons) via RoutedSPADialog. Only the reuse
// path lives here: "create new" is the Add Lessons menu's other item (AddLessonsMenu), so the
// dialog opens straight on the program picker. Same copy_resources submit; success reloads the
// page so CopyProgressModal picks up the in-flight copy, exactly like the legacy page. The legacy
// modal is still used by /admin.
const SPAReuseLessonsModal = () => {
  const { programId } = useParams<{ programId: string }>();
  const pid = Number(programId);
  const [sourceProgram, setSourceProgram] = useState<ISelectOption | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const { allLessons, units } = useProgramLessons(pid);
  const { isLoading: isLoadingPrograms, programs } = usePrograms();
  const options = useMemo(() => formatOptions(programs), [programs]);

  const copyLessonIds = useProgramContentStore(state => state.copyLessonIds);
  const copyUnitIds = useProgramContentStore(state => state.copyUnitIds);
  const reuseLessonIds = useProgramContentStore(state => state.reuseLessonIds);
  const reuseUnitIds = useProgramContentStore(state => state.reuseUnitIds);
  const isAnyChecked = useProgramContentStore(state => state.isAnyChecked);
  const reset = useProgramContentStore(state => state.reset);
  const setParentLessonIds = useProgramContentStore(state => state.setParentLessonIds);
  const setParentUnitIds = useProgramContentStore(state => state.setParentUnitIds);

  useEffect(() => { reset(); }, [reset]);

  useEffect(() => {
    setParentLessonIds(allLessons.map(lesson => lesson.id));
  }, [allLessons, setParentLessonIds]);

  useEffect(() => {
    setParentUnitIds(units.map(unit => unit.id));
  }, [setParentUnitIds, units]);

  const isValid = isAnyChecked();

  const handleSubmit = () => {
    setIsSubmitting(true);
    Axios
      .post(Routes.plato_api_copy_resources_program_path(pid), {
        copy_lesson_ids: copyLessonIds,
        copy_unit_ids: copyUnitIds,
        reuse_lesson_ids: reuseLessonIds,
        reuse_unit_ids: reuseUnitIds,
        source_program_id: sourceProgram?.value,
      })
      .then(() => window.location.reload())
      .catch(() => {
        setSubmitError(true);
        setIsSubmitting(false);
      });
  };

  // Until a source program is picked the body is short, so its scroll container would clip the
  // inline select menu — let the dialog overflow instead (as in SPAAddProgramModal), and only
  // switch the body back to scrolling once the units/lessons tree needs it.
  const overflowClass = sourceProgram ? undefined : 'tw-overflow-visible';

  return (
    <RoutedSPADialog>
      <SPADialog.Content className={overflowClass} size="sm">
        <SPADialog.Header>
          <SPADialog.Title>Reuse Lessons</SPADialog.Title>
          <SPADialog.Description className="tw-mt-1 tw-text-spa-secondary">
            Pick a program, then the units and lessons to reuse or copy into this one.
          </SPADialog.Description>
          <SPADialog.CloseButton />
        </SPADialog.Header>

        <SPADialog.Body className={overflowClass}>
          <div>
            <label className="tw-block" htmlFor="source_program_id">
              <span className="tw-text-sm tw-font-bold tw-text-spa-heading tw-mb-1.5 tw-block">
                Program
              </span>
              {/* No menuPortalTarget: the modal dialog puts pointer-events:none on <body>,
                  so a portaled menu would render but never receive clicks. */}
              <SPASelect
                inputId="source_program_id"
                isLoading={isLoadingPrograms}
                maxMenuHeight={240}
                name="source_program_id"
                options={options}
                placeholder="Select a Program"
                value={sourceProgram}
                onChange={(option) => {
                  setSourceProgram(option as ISelectOption | null);
                  reset();
                }}
              />
            </label>

            {sourceProgram && <SPAReuseContent programId={sourceProgram.value} />}
          </div>

          {submitError && (
            <p className="tw-m-0 tw-mt-4 tw-text-sm tw-text-spa-error">
              Couldn’t add the selected content. Try again.
            </p>
          )}
        </SPADialog.Body>

        <SPADialog.Footer>
          <SPADialog.Close asChild>
            <SPADialog.Button>Cancel</SPADialog.Button>
          </SPADialog.Close>
          <SPADialog.Button
            disabled={!isValid || isSubmitting}
            variant="primary"
            onClick={handleSubmit}
          >
            {isSubmitting ? 'Adding…' : 'Add Selected'}
          </SPADialog.Button>
        </SPADialog.Footer>
      </SPADialog.Content>
    </RoutedSPADialog>
  );
};

export default SPAReuseLessonsModal;
